import React, { useState, useCallback, useRef, useEffect } from 'react';

const panelStyle = {
  width: 280,
  background: '#1a1a2e',
  borderLeft: '1px solid rgba(102,126,234,0.15)',
  padding: 16,
  overflowY: 'auto',
  color: '#eee',
  fontFamily: 'sans-serif',
  borderRadius: '8px 0 0 8px',
};

const channelBtnStyle = (active) => ({
  padding: '4px 10px',
  background: active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
  color: active ? '#fff' : '#999',
  border: active ? 'none' : '1px solid #444',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 11,
  transition: 'all 0.2s',
});

const CHANNELS = ['rgb', 'red', 'green', 'blue'];
const CHANNEL_COLORS = { rgb: '#ccc', red: '#ff4444', green: '#44ff44', blue: '#4488ff' };
const SVG_SIZE = 248;

const INITIAL_CURVES = {
  rgb: [{ x: 0, y: 0 }, { x: 255, y: 255 }],
  red: [{ x: 0, y: 0 }, { x: 255, y: 255 }],
  green: [{ x: 0, y: 0 }, { x: 255, y: 255 }],
  blue: [{ x: 0, y: 0 }, { x: 255, y: 255 }],
};

/**
 * CurvesPanel — auto-applies when points change (debounced 500ms after mouse up).
 */
export default function CurvesPanel({ editor }) {
  const [activeChannel, setActiveChannel] = useState('rgb');
  const [curves, setCurves] = useState({ ...INITIAL_CURVES });
  const [dragging, setDragging] = useState(null);
  const svgRef = useRef(null);
  const mountedRef = useRef(false);
  const timerRef = useRef(null);
  // Track a change counter that increments on meaningful user actions (click add, drag end, remove)
  const [changeCount, setChangeCount] = useState(0);

  const toSvg = (val) => (val / 255) * SVG_SIZE;
  const fromSvg = (val) => Math.round(Math.min(255, Math.max(0, (val / SVG_SIZE) * 255)));

  // Auto-apply debounced on changeCount
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const payload = {};
      for (const ch of CHANNELS) {
        const pts = curves[ch].filter(
          (p) => !(p.x === 0 && p.y === 0) && !(p.x === 255 && p.y === 255),
        );
        payload[ch] = pts;
      }
      editor.curves({ curves: payload });
    }, 500);
    return () => clearTimeout(timerRef.current);
  }, [changeCount]);

  const points = curves[activeChannel];

  const handleSvgClick = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = e.clientX - rect.left;
    const svgY = e.clientY - rect.top;
    const x = fromSvg(svgX);
    const y = fromSvg(SVG_SIZE - svgY);

    const tooClose = points.some((p) => Math.abs(p.x - x) < 8);
    if (tooClose) return;
    if (points.length >= 16) return;

    const newPoints = [...points, { x, y }].sort((a, b) => a.x - b.x);
    setCurves((prev) => ({ ...prev, [activeChannel]: newPoints }));
    setChangeCount((c) => c + 1);
  }, [points, activeChannel]);

  const handleMouseDown = useCallback((idx) => {
    if (idx === 0 || idx === points.length - 1) return;
    setDragging(idx);
  }, [points.length]);

  const handleMouseMove = useCallback((e) => {
    if (dragging === null) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgY = e.clientY - rect.top;
    const y = fromSvg(SVG_SIZE - svgY);

    setCurves((prev) => {
      const pts = [...prev[activeChannel]];
      pts[dragging] = { ...pts[dragging], y: Math.min(255, Math.max(0, y)) };
      return { ...prev, [activeChannel]: pts };
    });
  }, [dragging, activeChannel]);

  const handleMouseUp = useCallback(() => {
    if (dragging !== null) {
      setChangeCount((c) => c + 1);
    }
    setDragging(null);
  }, [dragging]);

  const handleRemovePoint = useCallback((idx) => {
    if (idx === 0 || idx === points.length - 1) return;
    setCurves((prev) => {
      const pts = prev[activeChannel].filter((_, i) => i !== idx);
      return { ...prev, [activeChannel]: pts };
    });
    setChangeCount((c) => c + 1);
  }, [points.length, activeChannel]);

  const pathD = points
    .map((p, i) => {
      const sx = toSvg(p.x);
      const sy = SVG_SIZE - toSvg(p.y);
      return i === 0 ? `M ${sx} ${sy}` : `L ${sx} ${sy}`;
    })
    .join(' ');

  return (
    <div style={panelStyle}>
      <h3 style={{ margin: '0 0 8px', fontSize: 14, color: '#ddd' }}>〰️ Curves</h3>
      <div style={{ fontSize: 10, color: '#555', marginBottom: 10 }}>
        Auto-applies on change
      </div>

      {/* Channel selector */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {CHANNELS.map((ch) => (
          <button
            key={ch}
            style={channelBtnStyle(activeChannel === ch)}
            onClick={() => setActiveChannel(ch)}
          >
            {ch.toUpperCase()}
          </button>
        ))}
      </div>

      {/* SVG curve editor */}
      <svg
        ref={svgRef}
        width={SVG_SIZE}
        height={SVG_SIZE}
        style={{ background: '#0a0a15', borderRadius: 6, cursor: 'crosshair' }}
        onClick={handleSvgClick}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {[0.25, 0.5, 0.75].map((f) => (
          <React.Fragment key={f}>
            <line x1={f * SVG_SIZE} y1={0} x2={f * SVG_SIZE} y2={SVG_SIZE} stroke="#222" strokeWidth={0.5} />
            <line x1={0} y1={f * SVG_SIZE} x2={SVG_SIZE} y2={f * SVG_SIZE} stroke="#222" strokeWidth={0.5} />
          </React.Fragment>
        ))}
        <line x1={0} y1={SVG_SIZE} x2={SVG_SIZE} y2={0} stroke="#333" strokeWidth={1} strokeDasharray="4 4" />
        <path d={pathD} fill="none" stroke={CHANNEL_COLORS[activeChannel]} strokeWidth={2} />
        {points.map((p, idx) => (
          <circle
            key={idx}
            cx={toSvg(p.x)}
            cy={SVG_SIZE - toSvg(p.y)}
            r={5}
            fill={idx === 0 || idx === points.length - 1 ? '#444' : CHANNEL_COLORS[activeChannel]}
            stroke="#fff"
            strokeWidth={1}
            style={{ cursor: idx === 0 || idx === points.length - 1 ? 'default' : 'grab' }}
            onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(idx); }}
            onDoubleClick={(e) => { e.stopPropagation(); handleRemovePoint(idx); }}
          />
        ))}
      </svg>

      <p style={{ fontSize: 10, color: '#555', margin: '6px 0 0' }}>
        Click to add points. Drag to adjust. Double-click to remove.
      </p>
    </div>
  );
}
